import React, { useState, useEffect, useCallback } from 'react';
import { axiosAuth } from '../contexts/AuthContext';
import {
  RefreshCw,
  Loader2,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Wrench,
  Music,
  Copy,
  Check,
} from 'lucide-react';

const formatBytes = (n) => {
  if (!n && n !== 0) return '–';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (s) => {
  if (!s) return '–';
  try {
    return new Date(s).toLocaleString();
  } catch (_) {
    return s;
  }
};

const StatusBadge = ({ status, r2Exists, trackId }) => {
  // Compute the worst-case label
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-900/40 text-red-300 uppercase tracking-wider">
        <XCircle size={12} /> Rejected
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-900/40 text-red-300 uppercase tracking-wider">
        <XCircle size={12} /> Failed
      </span>
    );
  }
  if (r2Exists === false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-yellow-900/40 text-yellow-300 uppercase tracking-wider">
        <AlertTriangle size={12} /> Missing in R2
      </span>
    );
  }
  if (status === 'uploaded' && !trackId) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-yellow-900/40 text-yellow-300 uppercase tracking-wider">
        <AlertTriangle size={12} /> Not linked
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-900/40 text-green-300 uppercase tracking-wider">
      <CheckCircle2 size={12} /> OK
    </span>
  );
};

const CopyKey = ({ value, testid }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {
      /* ignore */
    }
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-xs font-mono text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
      title="Copy"
      data-testid={testid}
    >
      <span className="truncate max-w-[260px]">{value}</span>
      {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
    </button>
  );
};

const UploadsTab = () => {
  const [tab, setTab] = useState('audit'); // 'audit' | 'orphans'
  const [audits, setAudits] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [orphanStats, setOrphanStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState({});

  const fetchAudits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosAuth.get('/api/admin/uploads/audit?limit=200');
      setAudits(data.audits || []);
    } catch (err) {
      alert('Failed to load audit log: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrphans = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosAuth.get('/api/admin/uploads/orphans');
      setOrphans(data.orphans || []);
      setOrphanStats({
        total_r2: data.total_r2_audio_objects,
        referenced: data.total_track_references,
        orphan_count: data.orphan_count,
      });
    } catch (err) {
      alert('Failed to load orphans: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'audit') fetchAudits();
    else fetchOrphans();
  }, [tab, fetchAudits, fetchOrphans]);

  const handleRecover = async (storageKey) => {
    const title = window.prompt(
      'Title for the recovered track (you can edit it later in the Music tab):',
      'Recovered Track'
    );
    if (!title) return;
    setRecovering((s) => ({ ...s, [storageKey]: true }));
    try {
      await axiosAuth.post(
        `/api/admin/uploads/orphans/${encodeURIComponent(storageKey)}/recover?title=${encodeURIComponent(title)}`
      );
      alert('Orphan recovered as a draft track. Find it in the Music tab.');
      fetchOrphans();
    } catch (err) {
      alert('Recovery failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRecovering((s) => ({ ...s, [storageKey]: false }));
    }
  };

  return (
    <div data-testid="uploads-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-['Bebas_Neue']">Uploads</h2>
          <p className="text-sm text-[#A3A3A3]">
            Trace every audio upload end-to-end. Find files that made it to storage but never got
            attached to a track row, and recover them with one click.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={tab === 'audit' ? fetchAudits : fetchOrphans}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#D4AF37] text-sm uppercase tracking-wider transition-colors disabled:opacity-50"
            data-testid="refresh-uploads-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10">
        {[
          { id: 'audit', label: 'Audit Log' },
          { id: 'orphans', label: 'Find Orphans' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm uppercase tracking-wider transition-colors ${
              tab === t.id
                ? 'border-b-2 border-[#D4AF37] text-[#D4AF37]'
                : 'text-[#A3A3A3] hover:text-white'
            }`}
            data-testid={`uploads-subtab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'audit' && (
        <div className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-white/5" data-testid="audit-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0D0D0D] text-[#A3A3A3] text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 text-left">Kind</th>
                  <th className="px-4 py-3 text-left">Original filename</th>
                  <th className="px-4 py-3 text-left">Size</th>
                  <th className="px-4 py-3 text-left">Storage key</th>
                  <th className="px-4 py-3 text-left">Uploader</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Linked track</th>
                </tr>
              </thead>
              <tbody>
                {audits.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-[#737373]">
                      No upload audit records yet. Try uploading a track from the Music tab.
                    </td>
                  </tr>
                )}
                {audits.map((row) => (
                  <tr key={row.audit_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-[#A3A3A3] whitespace-nowrap">{formatDate(row.created_at)}</td>
                    <td className="px-4 py-3 text-[#A3A3A3]">{row.kind}</td>
                    <td className="px-4 py-3 text-white max-w-[220px] truncate" title={row.original_filename}>
                      {row.original_filename || '–'}
                    </td>
                    <td className="px-4 py-3 text-[#A3A3A3]">{formatBytes(row.file_size)}</td>
                    <td className="px-4 py-3">
                      {row.storage_key ? <CopyKey value={row.storage_key} testid={`copy-key-${row.audit_id}`} /> : '–'}
                    </td>
                    <td className="px-4 py-3 text-[#A3A3A3] max-w-[180px] truncate" title={row.uploader_email}>
                      {row.uploader_email || '–'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.storage_status} r2Exists={row.r2_exists} trackId={row.track_id} />
                    </td>
                    <td className="px-4 py-3">
                      {row.track_id ? (
                        <span className="text-green-300 text-xs font-mono">{row.track_title || row.track_id.slice(0, 8)}</span>
                      ) : (
                        <span className="text-[#737373] text-xs italic">none</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'orphans' && (
        <div data-testid="orphans-pane">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#1A1A1A] border border-white/5 p-4">
              <p className="text-xs text-[#737373] uppercase tracking-wider">Audio in R2</p>
              <p className="text-2xl font-['Bebas_Neue'] mt-1">{orphanStats.total_r2 ?? '–'}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-white/5 p-4">
              <p className="text-xs text-[#737373] uppercase tracking-wider">Referenced by tracks</p>
              <p className="text-2xl font-['Bebas_Neue'] mt-1 text-green-400">{orphanStats.referenced ?? '–'}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-yellow-900/40 p-4">
              <p className="text-xs text-yellow-500 uppercase tracking-wider">Orphans</p>
              <p className="text-2xl font-['Bebas_Neue'] mt-1 text-yellow-400" data-testid="orphan-count">
                {orphanStats.orphan_count ?? '–'}
              </p>
            </div>
          </div>

          <div className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-white/5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0D0D0D] text-[#A3A3A3] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Storage key</th>
                    <th className="px-4 py-3 text-left">Size</th>
                    <th className="px-4 py-3 text-left">Last modified</th>
                    <th className="px-4 py-3 text-left">Original filename</th>
                    <th className="px-4 py-3 text-left">Uploader</th>
                    <th className="px-4 py-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orphans.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-[#737373]">
                        🎉 No orphan audio files. Every R2 upload is linked to a track.
                      </td>
                    </tr>
                  )}
                  {orphans.map((o) => (
                    <tr key={o.storage_key} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <CopyKey value={o.storage_key} testid={`copy-orphan-${o.storage_key.split('/').pop()}`} />
                      </td>
                      <td className="px-4 py-3 text-[#A3A3A3]">{formatBytes(o.size)}</td>
                      <td className="px-4 py-3 text-[#A3A3A3] whitespace-nowrap">{formatDate(o.last_modified)}</td>
                      <td className="px-4 py-3 text-white max-w-[220px] truncate">
                        {o.audit?.original_filename || '–'}
                      </td>
                      <td className="px-4 py-3 text-[#A3A3A3] max-w-[160px] truncate">
                        {o.audit?.uploader_email || '–'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <a
                            href={o.audio_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-white/15 hover:border-[#D4AF37] uppercase tracking-wider transition-colors"
                            title="Preview audio in a new tab"
                          >
                            <Music size={12} /> Preview
                          </a>
                          <button
                            onClick={() => handleRecover(o.storage_key)}
                            disabled={!!recovering[o.storage_key]}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#D4AF37] text-black uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
                            data-testid={`recover-orphan-${o.storage_key.split('/').pop()}`}
                          >
                            {recovering[o.storage_key] ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
                            Recover
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {orphans.length > 0 && (
            <p className="text-xs text-[#737373] mt-4">
              <Search size={12} className="inline mr-1" />
              Tip: Click <strong>Preview</strong> to listen and confirm the file is yours, then click{' '}
              <strong>Recover</strong> to attach it as a new draft track. Edit title, genre, and cover art
              from the Music tab.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadsTab;
